import React, { useState, useRef, useEffect } from "react";
import { getEventPosition, computeOverlapColumns, formatDuration } from "../../utils/calendarUtils";

const HOUR_HEIGHT = 60;
const DRAG_THRESHOLD = 8;

const formatHour = (hour) => {
  if (hour === 0) return "12 AM";
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return "12 PM";
  return `${hour - 12} PM`;
};

const snapToQuarter = (y, hourHeight) => {
  const totalMinutes = Math.round((y / hourHeight) * 60 / 15) * 15;
  const clamped = Math.max(0, Math.min(23 * 60 + 45, totalMinutes));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const WeekView = ({
  currentDate,
  getEventsForDate,
  getColorForEvent,
  onOpenNewEvent,
  onOpenEditEvent,
  onRescheduleEvent,
  weekStartsMonday = false,
}) => {
  const getWeekDays = (date) => {
    const d = new Date(date);
    const offset = weekStartsMonday ? (d.getDay() + 6) % 7 : d.getDay();
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - offset);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      return day;
    });
  };

  const weekDays = getWeekDays(currentDate);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const weekViewRef = useRef(null);
  const columnRefs = useRef([]);
  const [now, setNow] = useState(new Date());
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (weekViewRef.current) weekViewRef.current.scrollTop = 8 * HOUR_HEIGHT;
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => { dragRef.current = drag; }, [drag]);

  const nowTop = (now.getHours() + now.getMinutes() / 60) * HOUR_HEIGHT;
  const todayStr = now.toDateString();

  const getDayIdxFromX = (clientX) => {
    for (let i = 0; i < columnRefs.current.length; i++) {
      const rect = columnRefs.current[i]?.getBoundingClientRect();
      if (rect && clientX >= rect.left && clientX < rect.right) return i;
    }
    return null;
  };

  const startDrag = (event, clientX, clientY, eventRect) => {
    const color = getColorForEvent(event);
    const d = {
      event,
      offsetY: clientY - eventRect.top,
      startClientY: clientY,
      currentClientY: clientY,
      currentClientX: clientX,
      fixedLeft: eventRect.left,
      fixedWidth: eventRect.width,
      eventHeight: eventRect.height,
      isDragging: false,
      color,
      isGradient: color.startsWith("linear-gradient"),
    };
    dragRef.current = d;
    setDrag(d);

    const onMove = (e) => {
      const cy = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
      const cx = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
      const cur = dragRef.current;
      if (!cur) return;
      if (!cur.isDragging && Math.abs(cy - cur.startClientY) > DRAG_THRESHOLD) {
        if (e.type === "touchmove") e.preventDefault();
        const updated = { ...cur, isDragging: true, currentClientY: cy, currentClientX: cx };
        dragRef.current = updated;
        setDrag(updated);
        return;
      }
      if (cur.isDragging) {
        if (e.type === "touchmove") e.preventDefault();
        const updated = { ...cur, currentClientY: cy, currentClientX: cx };
        dragRef.current = updated;
        setDrag(updated);
      }
    };

    const onEnd = () => {
      const cur = dragRef.current;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      if (!cur) return;
      dragRef.current = null;
      setDrag(null);

      if (cur.isDragging) {
        const dayIdx = getDayIdxFromX(cur.currentClientX);
        const targetDay = dayIdx !== null ? weekDays[dayIdx] : null;
        if (targetDay) {
          const colRect = columnRefs.current[dayIdx]?.getBoundingClientRect();
          if (colRect) {
            const rawTop = cur.currentClientY - colRect.top - cur.offsetY;
            const newTime = snapToQuarter(rawTop, HOUR_HEIGHT);
            onRescheduleEvent(cur.event, targetDay, newTime);
          }
        }
      } else {
        onOpenEditEvent(cur.event);
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
  };

  return (
    <>
      {/* Fixed drag overlay */}
      {drag?.isDragging && (
        <div
          className="fixed pointer-events-none z-50 text-[10px] sm:text-xs p-1 rounded font-bold overflow-hidden text-white shadow-xl"
          style={{
            top: drag.currentClientY - drag.offsetY,
            left: drag.currentClientX - drag.fixedWidth / 2,
            width: drag.fixedWidth,
            height: drag.eventHeight,
            opacity: 0.92,
            cursor: "grabbing",
            ...(drag.isGradient ? { background: drag.color } : { backgroundColor: drag.color }),
          }}
        >
          <div className="truncate">{drag.event.title}</div>
        </div>
      )}

      <div className="overflow-y-auto max-h-[500px] sm:max-h-[600px]" ref={weekViewRef}>
        <div className="overflow-x-auto">
          <div className="min-w-[600px]">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <div className="flex">
                <div className="flex-shrink-0 w-12 sm:w-16 border-r border-gray-200" />
                {weekDays.map((day, idx) => {
                  const isToday = day.toDateString() === todayStr;
                  return (
                    <div key={idx} className={`flex-1 text-center py-2 border-r border-gray-200 ${isToday ? "bg-blue-50" : ""}`}>
                      <div className="text-[10px] sm:text-xs text-gray-500">
                        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][day.getDay()]}
                      </div>
                      <div className={`text-sm sm:text-lg font-semibold ${isToday ? "text-blue-600" : "text-gray-700"}`}>
                        {day.getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* All-day row */}
              <div className="flex border-t border-gray-100 bg-gray-50">
                <div className="flex-shrink-0 w-12 sm:w-16 border-r border-gray-200 text-[9px] text-gray-400 px-1 py-1 flex items-center justify-end">all‑day</div>
                {weekDays.map((day, idx) => {
                  const allDayEvts = getEventsForDate(day).filter(e => e.all_day || (e.end_date && e.end_date > e.date));
                  return (
                    <div key={idx} className="flex-1 border-r border-gray-200 px-0.5 py-0.5 min-h-[18px]">
                      {allDayEvts.map(event => {
                        const c = getColorForEvent(event);
                        return (
                          <div key={event.id}
                            className="text-[9px] rounded px-1 mb-0.5 truncate cursor-pointer hover:opacity-80 font-bold"
                            style={{
                              ...(c.startsWith("linear-gradient") ? { background: c } : { backgroundColor: c }),
                              ...(event.canceled ? { color: '#7f1d1d', textDecoration: 'line-through' } : { color: '#fff' }),
                            }}
                            onClick={() => !event._virtual && onOpenEditEvent(event)}>
                            {event.title}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Time Grid */}
            <div className="flex">
              <div className="flex-shrink-0 w-12 sm:w-16 border-r border-gray-200">
                {hours.map(hour => (
                  <div key={hour} className="border-b border-gray-200 text-[10px] sm:text-xs text-gray-500 text-right pr-1 sm:pr-2 pt-1" style={{ height: HOUR_HEIGHT }}>
                    {formatHour(hour)}
                  </div>
                ))}
              </div>

              {weekDays.map((day, dayIdx) => {
                const isToday = day.toDateString() === todayStr;
                const allEvts = getEventsForDate(day);
                const timedEvents = allEvts.filter(e => !e.all_day && (!e.end_date || e.end_date === e.date));
                const overlapCols = computeOverlapColumns(timedEvents);

                return (
                  <div key={dayIdx}
                    ref={el => columnRefs.current[dayIdx] = el}
                    className="flex-1 relative border-r border-gray-200 cursor-pointer"
                    style={{ height: 24 * HOUR_HEIGHT }}
                    onClick={(e) => {
                      if (drag?.isDragging) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      onOpenNewEvent(day, snapToQuarter(e.clientY - rect.top, HOUR_HEIGHT));
                    }}
                  >
                    {hours.map(hour => (
                      <div key={hour} className="absolute w-full border-b border-gray-200" style={{ top: hour * HOUR_HEIGHT, height: HOUR_HEIGHT }} />
                    ))}

                    {isToday && (
                      <div className="absolute w-full z-20 pointer-events-none" style={{ top: nowTop }}>
                        <div className="absolute w-2 h-2 rounded-full bg-red-500 -left-1 -top-1" />
                        <div className="border-t-2 border-red-500 w-full" />
                      </div>
                    )}

                    {overlapCols.map(({ event, col, totalCols }) => {
                      if (drag?.isDragging && drag.event.id === event.id) return null;
                      const { top, height } = getEventPosition(event, HOUR_HEIGHT);
                      const c = getColorForEvent(event);
                      const isDraggable = false;
                      const duration = formatDuration(event.time, event.end_time);
                      return (
                        <div key={event.id}
                          className="absolute text-[10px] sm:text-xs p-1 rounded font-bold touch-manipulation overflow-hidden z-10"
                          style={{
                            top, height,
                            left: `${(col / totalCols) * 100}%`,
                            width: `calc(${(1 / totalCols) * 100}% - 2px)`,
                            cursor: isDraggable ? "grab" : "pointer",
                            ...(c.startsWith("linear-gradient") ? { background: c } : { backgroundColor: c }),
                            ...(event.canceled ? { color: '#7f1d1d' } : { color: '#fff' }),
                          }}
                          onMouseDown={isDraggable ? (e) => {
                            e.preventDefault(); e.stopPropagation();
                            startDrag(event, e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());
                          } : undefined}
                          onTouchStart={isDraggable ? (e) => {
                            e.stopPropagation();
                            startDrag(event, e.touches[0].clientX, e.touches[0].clientY, e.currentTarget.getBoundingClientRect());
                          } : undefined}
                          onClick={!isDraggable ? (e) => { e.stopPropagation(); if (!event._virtual) onOpenEditEvent(event); } : undefined}
                        >
                          <div className="truncate" style={event.canceled ? { textDecoration: 'line-through' } : {}}>{event.title}</div>
                          {duration && height > 36 && (
                            <div className="text-[9px] opacity-75 truncate">{duration}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WeekView;
